import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { FacebookIcon, GoogleIcon, InstagramIcon, tiktokIcon, XIcon, LinkedInIcon } from 'assets/images/integrations';
import { InfoOutlinedIcon } from 'assets/images/icons';
import AddDropdownHeader from '@components/AddDropdownHeader';
import { Typography } from '@components/Typography';
import { POST_CHANNEL, normalizePlatform } from '@constants/social-posts';
import { useLocations, useTranslation } from '@hooks';
import { isLocationDirectForPlatform, getConnectedPostingProfile } from '@hooks/useSocialPostsPlatforms';
import { accountService } from '@services';
import { Container, Header } from './styles';
import { resolveOverrideMessage, resolveOverrideMedia } from '../platformRules';
import { resolveLocationPlaceholders } from './resolveLocationPlaceholders';
import { formatPostDate, hasMediaContent, hasMessageContent, handleFromUrl } from './utils';
import StoryRingIcon from '@components/StoryRingIcon';
import PlatformCardFacebook from './PlatformCardFacebook';
import PlatformCardInstagram from './PlatformCardInstagram';
import PlatformCardGoogle from './PlatformCardGoogle';
import PlatformCardTikTok from './PlatformCardTikTok';
import PlatformCardX from './PlatformCardX';
import PlatformCardLinkedIn from './PlatformCardLinkedIn';
import PlatformCardStory from './PlatformCardStory';

const renderTikTokIcon = size => (
    <img src={tiktokIcon} alt="" width={size} height={size} style={{ display: 'inline-block' }} />
);

const PLATFORM_META = {
    [POST_CHANNEL.FACEBOOK]: {
        label: 'Facebook',
        renderIcon: size => <FacebookIcon width={size} height={size} />,
        Card: PlatformCardFacebook,
    },
    [POST_CHANNEL.INSTAGRAM]: {
        label: 'Instagram',
        renderIcon: size => <InstagramIcon width={size} height={size} />,
        Card: PlatformCardInstagram,
    },
    [POST_CHANNEL.GOOGLE]: {
        label: 'Google',
        renderIcon: size => <GoogleIcon width={size} height={size} />,
        Card: PlatformCardGoogle,
    },
    [POST_CHANNEL.TIKTOK]: {
        label: 'TikTok',
        renderIcon: renderTikTokIcon,
        Card: PlatformCardTikTok,
    },
    [POST_CHANNEL.X]: {
        label: 'X',
        renderIcon: size => <XIcon width={size} height={size} />,
        Card: PlatformCardX,
    },
    [POST_CHANNEL.LINKEDIN]: {
        label: 'LinkedIn',
        renderIcon: size => <LinkedInIcon width={size} height={size} />,
        Card: PlatformCardLinkedIn,
    },
    [POST_CHANNEL.FACEBOOK_STORY]: {
        label: 'Facebook Story',
        renderIcon: size => (
            <StoryRingIcon size={size}>
                <FacebookIcon />
            </StoryRingIcon>
        ),
        Card: PlatformCardStory,
    },
    [POST_CHANNEL.INSTAGRAM_STORY]: {
        label: 'Instagram Story',
        renderIcon: size => (
            <StoryRingIcon size={size}>
                <InstagramIcon />
            </StoryRingIcon>
        ),
        Card: PlatformCardStory,
    },
};

const PLATFORM_ORDER = [
    POST_CHANNEL.FACEBOOK,
    POST_CHANNEL.FACEBOOK_STORY,
    POST_CHANNEL.INSTAGRAM,
    POST_CHANNEL.INSTAGRAM_STORY,
    POST_CHANNEL.GOOGLE,
    POST_CHANNEL.TIKTOK,
    POST_CHANNEL.X,
    POST_CHANNEL.LINKEDIN,
];

const HintCaption = ({ children }) => (
    <Typography
        variant="caption"
        sx={{ color: 'var(--text-secondary)', display: 'block', textAlign: 'center', padding: '0 24px' }}
    >
        <InfoOutlinedIcon style={{ transform: 'translateY(4px)', marginRight: 8 }} />
        {children}
    </Typography>
);

const PostPreview = ({
    platforms,
    companies,
    message,
    media,
    button,
    scheduledTime,
    overrides = [],
    skipEligibility = false,
    tiktokCreators = [],
}) => {
    const { t } = useTranslation();
    const { locationsMap } = useLocations();

    const overrideByPlatform = useMemo(() => {
        const map = {};
        (overrides || []).forEach(o => {
            map[normalizePlatform(o.platform)] = o;
        });
        return map;
    }, [overrides]);

    const targets = useMemo(() => {
        if (!platforms?.length || !companies?.length) return [];
        const normalized = platforms.map(normalizePlatform).filter(p => PLATFORM_META[p]);
        const sortedPlatforms = [...normalized].sort((a, b) => PLATFORM_ORDER.indexOf(a) - PLATFORM_ORDER.indexOf(b));
        const out = [];
        for (const platform of sortedPlatforms) {
            for (const company of companies) {
                const realLocation = locationsMap[company.id];
                if (skipEligibility) {
                    // Stub for historical posts whose location is no longer accessible;
                    // the lazy detail fetch upgrades it when (and if) it lands.
                    const location = realLocation || {
                        companyId: company.id,
                        locationName: company.label || '',
                        internalName: company.label || '',
                    };
                    out.push({ platform, location });
                    continue;
                }
                if (!realLocation || !isLocationDirectForPlatform(realLocation, platform)) continue;
                out.push({ platform, location: realLocation });
            }
        }
        return out;
    }, [platforms, companies, locationsMap, skipEligibility]);

    const [selectedIndex, setSelectedIndex] = useState(0);
    useEffect(() => {
        if (selectedIndex > targets.length - 1) {
            setSelectedIndex(Math.max(0, targets.length - 1));
        }
    }, [targets.length, selectedIndex]);

    // Preview shows when any target has renderable content — either base message/media or a
    // platform-specific override. Without this widening the panel would stay hidden when the
    // user only filled an override (e.g. Instagram-only caption with no base message).
    const anyOverrideHasContent = (overrides || []).some(o => hasMessageContent(o.message) || hasMediaContent(o.media));
    const showPreview =
        targets.length > 0 && (hasMessageContent(message) || hasMediaContent(media) || anyOverrideHasContent);

    const safeIndex = targets.length ? Math.min(selectedIndex, targets.length - 1) : -1;
    const current = showPreview && safeIndex >= 0 ? targets[safeIndex] : null;
    const currentCompanyId = current?.location?.companyId ?? null;

    // account.companies[] omits logoUrl and most placeholder fields, so the preview lazily
    // fetches the full location detail for the selected target and caches it by id.
    const detailCacheRef = useRef(new Map());
    const [, bumpRender] = useReducer(x => x + 1, 0);
    useEffect(() => {
        if (currentCompanyId == null) return undefined;
        if (detailCacheRef.current.has(currentCompanyId)) return undefined;
        let cancelled = false;
        accountService
            .getLocation(currentCompanyId)
            .then(detail => {
                if (cancelled || !detail) return;
                detailCacheRef.current.set(currentCompanyId, detail);
                bumpRender();
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [currentCompanyId]);

    if (!current) {
        return (
            <Container>
                <HintCaption>{t('SocialPosts.preview.emptyHint')}</HintCaption>
            </Container>
        );
    }

    const meta = PLATFORM_META[current.platform];
    const PlatformCard = meta.Card;

    const effectiveLocation = detailCacheRef.current.get(currentCompanyId) || current.location;

    // locationName is what followers see; internalName is admin-only.
    const pageName =
        effectiveLocation.locationName || effectiveLocation.internalName || effectiveLocation.brand?.title || '';
    const locationLogo = effectiveLocation.logoUrl || null;
    const identityProfile =
        current.platform === POST_CHANNEL.GOOGLE
            ? null
            : getConnectedPostingProfile(effectiveLocation, current.platform);
    // TikTok identity comes from the creator_info snapshot fetched for this editor session, which
    // is what the upload page must show; the stored profile only stands in until it arrives.
    const tiktokCreator =
        current.platform === POST_CHANNEL.TIKTOK ? tiktokCreators.find(c => c.locationId === currentCompanyId) : null;
    const handle = tiktokCreator?.username || handleFromUrl(identityProfile?.businessUrl);
    const displayName = tiktokCreator?.displayName || identityProfile?.title || pageName;
    const avatar = tiktokCreator?.avatarUrl || locationLogo;
    const overrideForPlatform = overrideByPlatform[current.platform];
    const baseMessage = resolveOverrideMessage(overrideForPlatform, message);
    const effectiveMedia = resolveOverrideMedia(overrideForPlatform, media);
    const resolvedMessage = resolveLocationPlaceholders(
        baseMessage,
        effectiveLocation,
        effectiveLocation.brand?.title || '',
    );
    const dateLabel = formatPostDate(scheduledTime);

    const options = targets.map((tgt, i) => ({
        value: i,
        label: `${PLATFORM_META[tgt.platform].label} · ${tgt.location.internalName || ''}`,
    }));

    return (
        <Container>
            <Header>
                <AddDropdownHeader
                    options={options}
                    onAdd={setSelectedIndex}
                    buttonText={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            {meta.renderIcon(16)}
                            <span>
                                {meta.label} · {current.location.internalName}
                            </span>
                        </span>
                    }
                    buttonProps={{ size: 'small', sx: { backgroundColor: '#fff' } }}
                />
            </Header>

            <PlatformCard
                platform={current.platform}
                pageName={displayName}
                handle={handle}
                locationLogo={avatar}
                message={resolvedMessage}
                media={effectiveMedia}
                dateLabel={dateLabel}
                button={current.platform === POST_CHANNEL.GOOGLE ? button : 0}
            />
        </Container>
    );
};

export default PostPreview;
